import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Play, CheckCircle, ArrowLeft, DollarSign, ArrowRight, Monitor } from 'lucide-react';

const ENCODERS = [
  { value: 'obs', label: 'OBS Studio', desc: 'Open Broadcaster Software' },
  { value: 'streamlabs', label: 'Streamlabs Desktop', desc: 'Streamlabs OBS' },
  { value: 'xsplit', label: 'XSplit', desc: 'XSplit Broadcaster' },
  { value: 'prism', label: 'Prism Live', desc: 'Prism Live Studio' },
  { value: 'vmix', label: 'vMix', desc: 'Professional live production' },
  { value: 'other', label: 'Other', desc: 'Different software' },
];

const CREATOR_PERKS = [
  '70% revenue share — the highest in the industry',
  'One-click "Cut to Commercial" during natural stream breaks',
  'Real-time earnings dashboard with per-impression tracking',
  'Brands compete to bid on YOUR audience — you set the value',
];

export default function CreatorSignup() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [encoder, setEncoder] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleNext = () => {
    if (!name || !email || !password) { toast.error('Fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be 6+ characters'); return; }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!encoder) { toast.error('Select your streaming software'); return; }
    setLoading(true);
    try {
      const res = await register(name, email, password, ['streamer']);
      // Save encoder and profile data
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const axios = (await import('axios')).default;
        await axios.post(`${API}/streamer-profiles`, {
          displayName: name,
          encoder: encoder,
        }, { headers: { Authorization: `Bearer ${res.token}` } });
      } catch (e) { /* profile will be created later if this fails */ }
      // Don't navigate — show application submitted screen
      // Log them out since they can't access the platform yet
      localStorage.removeItem('ayo_token');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <div className="max-w-md text-center animate-fade-in-up">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-white uppercase mb-3">Application Submitted</h1>
          <p className="text-gray-400 mb-6 leading-relaxed">
            Thanks for applying to AYO Source, <span className="text-white font-medium">{name}</span>. Our team will review your application and verify your streaming data. You'll receive access once approved.
          </p>
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-4 mb-6 text-left">
            <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-2">Application Summary</p>
            <div className="space-y-1 text-xs font-mono">
              <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="text-white">{name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="text-white">{email}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Encoder</span><span className="text-white">{ENCODERS.find(e => e.value === encoder)?.label || encoder}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="text-yellow-400">Pending Review</span></div>
            </div>
          </div>
          <Link to="/landing">
            <Button variant="ghost" className="text-gray-400 hover:text-white border border-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to AYO Source
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left: Value Props */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#0a0a0a] border-r border-white/5 p-12">
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF3B30]/30 rounded-full bg-[#FF3B30]/5 mb-8">
            <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
            <span className="text-xs font-mono text-[#FF3B30] uppercase tracking-widest">For Creators</span>
          </div>

          <h2 className="font-heading text-3xl font-black tracking-tighter text-white uppercase mb-6">
            Your Audience.<br />Your Revenue.
          </h2>

          <div className="space-y-4 mb-10">
            {CREATOR_PERKS.map((perk, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">{perk}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <p className="text-2xl font-black text-[#FF3B30] font-mono">70%</p>
              <p className="text-xs text-gray-500 font-mono uppercase">Your Share</p>
            </div>
            <div className="p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <p className="text-2xl font-black text-white font-mono">&lt;1s</p>
              <p className="text-xs text-gray-500 font-mono uppercase">Trigger Speed</p>
            </div>
          </div>

          <div className="mt-8 p-4 border border-white/10 rounded-lg bg-white/[0.02]">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm text-white font-medium">Example: 2,000 CCV streamer</p>
                <p className="text-xs text-gray-400 font-mono">$15 CPM floor x 3 ad breaks/stream = $90/stream in your pocket</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <Link to="/landing" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to AYO Source
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
              <Play className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Creator Sign Up</h1>
              <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">Streamer Account</p>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8 mt-4">
            <div className={`h-1 flex-1 rounded-full transition-all ${step >= 1 ? 'bg-[#FF3B30]' : 'bg-white/10'}`} />
            <div className={`h-1 flex-1 rounded-full transition-all ${step >= 2 ? 'bg-[#FF3B30]' : 'bg-white/10'}`} />
          </div>

          {step === 1 ? (
            /* STEP 1: Account Details */
            <div className="space-y-5">
              <p className="text-sm text-gray-400 mb-4">Step 1 of 2 — Account Details</p>
              <div className="space-y-2">
                <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Your Name / Stream Name</Label>
                <Input data-testid="creator-signup-name" type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="YourStreamName" className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Email</Label>
                <Input data-testid="creator-signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@stream.com" className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Password</Label>
                <Input data-testid="creator-signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min 6 characters" className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12" />
              </div>
              <Button data-testid="creator-signup-next" onClick={handleNext}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest rounded-none transition-all duration-200">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            /* STEP 2: Encoder Selection */
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">Step 2 of 2 — Streaming Software</p>
                <button onClick={() => setStep(1)} className="text-xs text-gray-500 hover:text-white transition-colors">Back</button>
              </div>
              <p className="text-white text-base font-medium">What do you use to stream?</p>
              <div className="space-y-2">
                {ENCODERS.map(enc => (
                  <button
                    key={enc.value}
                    data-testid={`encoder-${enc.value}`}
                    onClick={() => setEncoder(enc.value)}
                    className={`w-full flex items-center gap-4 p-4 border rounded-lg text-left transition-all ${
                      encoder === enc.value
                        ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    }`}
                  >
                    <Monitor className={`w-5 h-5 flex-shrink-0 ${encoder === enc.value ? 'text-[#FF3B30]' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm font-medium ${encoder === enc.value ? 'text-white' : 'text-gray-300'}`}>{enc.label}</p>
                      <p className="text-xs text-gray-500">{enc.desc}</p>
                    </div>
                    {encoder === enc.value && <CheckCircle className="w-4 h-4 text-[#FF3B30] ml-auto" />}
                  </button>
                ))}
              </div>
              <Button data-testid="creator-signup-submit" onClick={handleSubmit} disabled={loading || !encoder}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest rounded-none transition-all duration-200">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                  <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Create Account</span>}
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">Already have an account? <Link to="/login" className="text-[#FF3B30] hover:underline font-medium">Sign In</Link></p>
            <p className="text-gray-600 text-xs mt-2">Want to advertise? <Link to="/signup/brand" className="text-[#7C3AED] hover:underline">Brand Sign Up</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
